"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Consultation() {
  const container = useRef<HTMLDivElement>(null);
  const headlineWords = [{
    text: "Start",
    italic: false,
    marked: false
  }, {
    text: "your",
    italic: false,
    marked: false
  }, {
    text: "SEO",
    italic: false,
    marked: false
  }, {
    text: "growth",
    italic: true,
    marked: true
  }, {
    text: "journey.",
    italic: false,
    marked: false
  }];
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".cta-kicker", {
        y: 12
      });
      gsap.set(".cta-word", {
        y: 38
      });
      gsap.set(".cta-button", {
        y: 8
      });
      gsap.set(".cta-response", {
        y: 8
      });
      gsap.set(".marker-path", {
        drawSVG: "0%"
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.to(".cta-kicker", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        delay: 0.1
      }).to(".cta-word", {
        opacity: 1,
        y: 0,
        duration: 0.72,
        stagger: 0.055
      }, "-=0.2").to(".marker-svg", {
        opacity: 1,
        duration: 0.1
      }, "-=0.1").to(".marker-path", {
        strokeDashoffset: 0,
        duration: 0.7,
        ease: "power2.out"
      }, "-=0.05").to(".cta-button", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12
      }, "-=0.3").to(".cta-response", {
        opacity: 1,
        y: 0,
        duration: 0.5
      }, "-=0.2");
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".cta-kicker", ".cta-word", ".cta-button", ".cta-response", ".marker-svg"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".marker-path", {
        strokeDashoffset: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30577" id="consultationcta" className="bg-background py-28 md:py-52">
      <div ref={container} className="mx-auto max-w-[1200px] px-6 md:px-12 text-center">
        <p className="cta-kicker font-sans text-[15px] tracking-[0.18em] uppercase text-muted-foreground" style={{
        opacity: 0
      }}>
          {"— Book your free strategy session"}
        </p>

        <h2 className="font-serif mt-12 font-normal text-foreground" style={{
        fontSize: "clamp(36px, 4.5vw, 58px)",
        letterSpacing: "-0.035em",
        lineHeight: "0.92"
      }}>
          <span className="flex flex-wrap justify-center gap-x-[0.28em] gap-y-2">
            {headlineWords.map((word, i) => <span key={i} data-index={i} className="cta-word relative inline-block" style={{
            opacity: 0
          }}>
                <span className={word.italic ? "italic" : ""}>
                  {word.text}
                </span>
                {word.marked && <svg className="marker-svg absolute left-0 right-0 pointer-events-none" style={{
              opacity: 0,
              bottom: "-0.08em",
              width: "100%",
              height: "0.28em"
            }} viewBox="0 0 200 24" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path className="marker-path" d="M4 14 C 30 8, 60 18, 92 12 C 128 6, 158 16, 196 10" stroke="hsl(340 75% 65%)" strokeWidth="8" strokeLinecap="round" strokeDasharray="220" strokeDashoffset="220" style={{
                filter: "url(#rough)"
              }} />
                    <defs>
                      <filter id="rough" x="-5%" y="-50%" width="110%" height="200%">
                        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
                        <feDisplacementMap in="SourceGraphic" scale="2.4" />
                      </filter>
                    </defs>
                  </svg>}
              </span>)}
          </span>
        </h2>

        <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center sm:items-stretch flex-wrap">
          <div className="cta-button w-full sm:w-auto max-w-[320px]" style={{
          opacity: 0
        }}>
            <Button asChild className="group h-auto w-full sm:w-auto rounded-lg px-8 py-[18px] text-base font-medium tracking-wide transition-colors duration-300 bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                <span>Book a consultation</span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <div className="cta-button w-full sm:w-auto max-w-[320px]" style={{
          opacity: 0
        }}>
            <Button asChild variant="outline" className="h-auto w-full sm:w-auto rounded-lg border-[1.5px] bg-transparent px-8 py-[18px] text-base font-medium tracking-wide transition-colors duration-300 border-primary text-primary hover:bg-primary/5">
              <Link to="/CaseStudiesPage">View case studies</Link>
            </Button>
          </div>
        </div>

        <p className="cta-response mt-8 font-sans text-[15px] tracking-wide text-muted-foreground" style={{
        opacity: 0
      }}>
          I reply within 48 hours.
        </p>
      </div>
    </section>;
}